import { Link } from "react-router-dom";
import { useT } from "../../i18n/LanguageContext";
import { track } from "../../lib/analytics";

/**
 * Compact case-study card used on the /case-studies index and in the
 * "Related case studies" strip at the bottom of each detail page.
 * Portfolio.jsx keeps its own alternating two-column layout for the Home section.
 */
export default function CaseStudyCard({ asset, copy, index, testid = "case-study-card", source = "index" }) {
    const { t } = useT();
    const first = asset.images?.[0] || "";
    const isLocalPng = first.startsWith("/portfolio/") && first.endsWith(".png");
    const avifSrc = isLocalPng ? first.replace(/\.png$/, ".avif") : null;
    const webpSrc = isLocalPng ? first.replace(/\.png$/, ".webp") : null;
    const industryLabel = t(`portfolio.filters.industries.${asset.industry}`);

    return (
        <Link
            to={`/case-studies/${asset.slug}`}
            data-testid={testid}
            onClick={() =>
                track("case_study_opened", {
                    slug: asset.slug,
                    industry: asset.industry,
                    source,
                })
            }
            className="group block bg-[var(--sand)] border border-warm hover:border-ink transition-colors"
        >
            <div className="aspect-[16/10] overflow-hidden bg-ink/[0.03] relative">
                <picture>
                    {avifSrc && <source srcSet={avifSrc} type="image/avif" />}
                    {webpSrc && <source srcSet={webpSrc} type="image/webp" />}
                    <img
                        src={first}
                        alt={`${copy.client} — preview`}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-contain p-4 md:p-6 group-hover:scale-[1.02] transition-transform duration-500"
                    />
                </picture>
                {!asset.href && (
                    <div className="absolute top-3 left-3 bg-[var(--sand)] text-ink px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase font-medium border border-ink/10">
                        {t("portfolio.privateBadge")}
                    </div>
                )}
            </div>
            <div className="p-6 md:p-7">
                <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="font-mono text-terracotta text-xs tracking-[0.16em]">
                        {copy.n} · {copy.client}
                    </span>
                    {typeof index === "number" && (
                        <span className="font-mono text-[10px] text-secondary tracking-[0.2em]">
                            {String(index + 1).padStart(2, "0")}
                        </span>
                    )}
                </div>
                {copy.outcomeKicker && (
                    <div
                        className="font-mono uppercase text-terracotta text-[0.7rem] sm:text-xs tracking-[0.18em] leading-snug mb-3 border-l-2 border-terracotta pl-3"
                        data-testid="case-study-outcome-kicker"
                    >
                        {copy.outcomeKicker}
                    </div>
                )}
                <h3 className="font-serif text-xl md:text-2xl leading-tight text-ink tracking-tight">
                    {copy.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-secondary line-clamp-3">
                    {copy.intro}
                </p>
                <div className="mt-5 flex flex-wrap gap-1.5">
                    <span className="text-[0.65rem] font-mono tracking-wide uppercase border border-warm text-ink/60 px-2 py-0.5">
                        {industryLabel}
                    </span>
                    {(asset.productTypes || []).slice(0, 3).map((pt) => (
                        <span
                            key={pt}
                            className="text-[0.65rem] font-mono tracking-wide uppercase border border-warm text-ink/80 px-2 py-0.5"
                        >
                            {t(`portfolio.filters.productTypes.${pt}`)}
                        </span>
                    ))}
                </div>
                <div className="mt-6 flex items-center gap-2 text-sm text-ink font-medium">
                    {t("portfolio.readCaseStudy")}
                    <span
                        aria-hidden
                        className="inline-block transition-transform group-hover:translate-x-1"
                    >
                        →
                    </span>
                </div>
            </div>
        </Link>
    );
}
